/**
 * PlacesSearch - Google Places Autocomplete for 3D Map
 * RoofMapper v3 - NIKO Energy
 */

import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Search } from 'lucide-react';

interface PlacesSearchProps {
  onPlaceSelected: (location: { lat: number; lng: number; address: string }) => void;
  disabled?: boolean;
  className?: string;
}

export default function PlacesSearch({ onPlaceSelected, disabled, className = '' }: PlacesSearchProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const autocompleteRef = useRef<any>(null);

  // Detect if input looks like coordinates (e.g., "19.4224, -99.2151")
  const looksLikeCoordinates = (text: string): boolean => {
    const cleaned = text.trim();
    // Match patterns like: "19.4224, -99.2151" or "19.4224,-99.2151" or "19.4224 -99.2151"
    const coordPattern = /^-?\d+\.?\d*[,\s]+-?\d+\.?\d*$/;
    return coordPattern.test(cleaned);
  };

  // Handle smart search (auto-detect coordinates vs address)
  const handleSmartSearch = (text: string) => {
    if (!text.trim()) return;

    // Check if it looks like coordinates
    if (looksLikeCoordinates(text)) {
      // Parse as coordinates
      const cleaned = text.trim().replace(/\s+/g, ' ');
      const parts = cleaned.split(/[,\s]+/);
      
      if (parts.length === 2 && parts[0] && parts[1]) {
        const lat = parseFloat(parts[0]);
        const lng = parseFloat(parts[1]);
        
        if (!isNaN(lat) && !isNaN(lng) && 
            lat >= -90 && lat <= 90 && 
            lng >= -180 && lng <= 180) {
          
          onPlaceSelected({ 
            lat, 
            lng, 
            address: `${lat.toFixed(6)}, ${lng.toFixed(6)}` 
          });
          
          // Clear input
          if (inputRef.current) {
            inputRef.current.value = '';
          }
          return;
        }
      }
    }
    
    // Otherwise, let Places Autocomplete handle it (address search)
  };

  useEffect(() => {
    const initAutocomplete = async () => {
      if (!inputRef.current) {
        return;
      }

      if (!window.google?.maps?.importLibrary) {
        return;
      }

      try {
        setIsLoading(true);

        // Import Places library explicitly  
        const { Autocomplete } = await window.google.maps.importLibrary('places') as any;

        // Create autocomplete - use only 'geocode' type (address can't be mixed)
        const autocomplete = new Autocomplete(inputRef.current, {
          fields: ['geometry', 'formatted_address', 'name'],
          types: ['geocode'],  // Changed: don't mix 'address' with 'geocode'
        });

        autocompleteRef.current = autocomplete;

        // Listen for place selection
        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();

          if (!place.geometry || !place.geometry.location) {
            console.error('❌ No geometry found for place');
            return;
          }

          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          const address = place.formatted_address || place.name || '';

          onPlaceSelected({ lat, lng, address });

          // Clear input after selection
          if (inputRef.current) {
            inputRef.current.value = '';
          }
        });

        setIsLoading(false);
      } catch (error) {
        console.error('❌ Error initializing Places Autocomplete:', error);
        setIsLoading(false);
      }
    };

    // Try multiple times with increasing delays
    let attempts = 0;
    const maxAttempts = 5;
    
    const tryInit = () => {
      attempts++;
      
      if (window.google?.maps?.importLibrary !== undefined) {
        initAutocomplete();
      } else if (attempts < maxAttempts) {
        setTimeout(tryInit, 1000 * attempts); // Exponential backoff
      } else {
        console.error('❌ Failed to initialize Places after', maxAttempts, 'attempts');
      }
    };

    const timeout = setTimeout(tryInit, 2000);
    return () => clearTimeout(timeout);
  }, [onPlaceSelected]);

  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500 z-10" />
      <input
        ref={inputRef}
        type="text"
        placeholder="🗺️ Address or lat, lng..."
        disabled={disabled || isLoading}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            const value = (e.target as HTMLInputElement).value;
            handleSmartSearch(value);
          }
        }}
        className={`w-full pl-10 pr-4 py-2.5 border-2 border-gray-300 rounded-lg text-sm font-medium
                 focus:outline-none focus:border-black
                 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${className}`}
      />
    </div>
  );
}

